import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { Progress } from "../../components/ui/progress";
import { toast } from "../../components/ui/use-toast";
import { neuralNetworkService, WriteupEntry, LearningAnalytics } from "../../lib/firebase";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Edit, EyeOff, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { serverTimestamp } from "firebase/firestore";
import { user } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/use-toast";

export const AdminNeuralNetwork = () => {
  const [writeups, setWriteups] = useState<WriteupEntry[]>([]);
  const [analytics, setAnalytics] = useState<LearningAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("writeups");
  const [editingWriteup, setEditingWriteup] = useState<WriteupEntry | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "vulnerability" as WriteupEntry['category'],
    tags: "",
    difficulty: "beginner" as WriteupEntry['difficulty'],
    author: "",
    isActive: true
  });
  const [filters, setFilters] = useState({
    category: "all",
    difficulty: "all",
    isActive: true
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWriteupId, setSelectedWriteupId] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Convert "all" values to empty strings for the service call
      const serviceFilters = {
        category: filters.category === "all" ? "" : filters.category,
        difficulty: filters.difficulty === "all" ? "" : filters.difficulty,
        isActive: filters.isActive
      };
      
      const [writeupsData, analyticsData] = await Promise.all([
        neuralNetworkService.getWriteups(serviceFilters),
        neuralNetworkService.getLearningAnalytics()
      ]);
      setWriteups(writeupsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Error",
        description: "Failed to load neural network data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddWriteup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast({
        title: "Validation Error",
        description: "Title and content are required",
        variant: "destructive",
      });
      return;
    }

    try {
      await neuralNetworkService.addWriteup({
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      });
      
      toast({
        title: "Success",
        description: "Writeup added successfully",
      });
      
      setFormData({
        title: "",
        content: "",
        category: "vulnerability",
        tags: "",
        difficulty: "beginner",
        author: "",
        isActive: true
      });
      setShowAddForm(false);
      loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add writeup",
        variant: "destructive",
      });
    }
  };

  const handleEditWriteup = async (id: string, updatedData: any) => {
    try {
      await updateDoc(doc(db, "neuralWriteups", id), updatedData);
      toast({ title: "Writeup updated!" });
      loadData();
    } catch (e) {
      toast({ title: "Error updating writeup", variant: "destructive" });
    }
  };

  const handleDeactivateWriteup = async (id: string) => {
    try {
      await updateDoc(doc(db, "neuralWriteups", id), { isActive: false });
      toast({ title: "Writeup deactivated!" });
      loadData();
    } catch (e) {
      toast({ title: "Error deactivating writeup", variant: "destructive" });
    }
  };

  const handleDeleteWriteup = async (id: string) => {
    try {
      await deleteDoc(doc(db, "neuralWriteups", id));
      toast({ title: "Writeup deleted!" });
      loadData();
    } catch (e) {
      toast({ title: "Error deleting writeup", variant: "destructive" });
    }
  };

  const getLearningScoreColor = (score: number) => {
    if (score >= 0.8) return "bg-green-500";
    if (score >= 0.6) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const WriteupCard = ({ writeup }: { writeup: WriteupEntry }) => (
    <motion.div
      key={writeup.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#f8fbe7] rounded-xl p-6 border border-[#daed6e]"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-bold text-lg text-black">{writeup.title}</h3>
            <Badge className={getDifficultyColor(writeup.difficulty)}>
              {writeup.difficulty}
            </Badge>
            <Badge variant="outline">{writeup.category}</Badge>
            {!writeup.isActive && <Badge variant="secondary">Inactive</Badge>}
          </div>
          <p className="text-sm text-gray-600 mb-2">
            By {writeup.author || 'Anonymous'} • {(() => {
              try {
                const date = writeup.createdAt;
                if (!date) return 'Unknown date';
                
                // Handle different date formats
                if (typeof date === 'string') {
                  return new Date(date).toLocaleDateString();
                } else if (date instanceof Date) {
                  return date.toLocaleDateString();
                } else {
                  // Assume it's a Firestore Timestamp or similar object with toDate method
                  return (date as any).toDate().toLocaleDateString();
                }
              } catch (error) {
                return 'Invalid date';
              }
            })()}
          </p>
          <div className="flex flex-wrap gap-1 mb-3">
            {writeup.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium">Learning Score:</span>
            <Badge className={getLearningScoreColor(writeup.learningScore)}>
              {(writeup.learningScore * 100).toFixed(0)}%
            </Badge>
          </div>
          <div className="text-sm text-gray-600">
            Used {writeup.usageCount} times
          </div>
        </div>
      </div>
      
      <p className="text-gray-700 mb-4 line-clamp-3">{writeup.content}</p>
      
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setEditingWriteup(writeup);
            setEditDialogOpen(true);
          }}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDeactivateWriteup(writeup.id)}
          disabled={!writeup.isActive}
        >
          Deactivate
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            setSelectedWriteupId(writeup.id);
            setDeleteDialogOpen(true);
          }}
        >
          Delete
        </Button>
      </div>
    </motion.div>
  );

  const DeleteConfirmationDialog = () => (
    <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this writeup? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (selectedWriteupId) {
                handleDeleteWriteup(selectedWriteupId);
                setDeleteDialogOpen(false);
                setSelectedWriteupId(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const EditWriteupDialog = () => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      content: '',
      tags: [] as string[],
      category: ''
    });

    useEffect(() => {
      if (editingWriteup) {
        setFormData({
          title: editingWriteup.title || '',
          description: editingWriteup.description || '',
          content: editingWriteup.content || '',
          tags: editingWriteup.tags || [],
          category: editingWriteup.category || ''
        });
      }
    }, [editingWriteup]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleEditWriteup(editingWriteup.id, formData);
    };

    return (
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Writeup</DialogTitle>
            <DialogDescription>
              Update the writeup information below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={10}
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags.join(', ')}
                onChange={(e) => setFormData({
                  ...formData, 
                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                })}
                placeholder="tag1, tag2, tag3"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingWriteup(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#daed6e] mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading Neural Network Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/admin')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Admin Dashboard</span>
              <div 
                onClick={() => navigate('/admin')}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="mb-8 flex items-center gap-2">
          <button 
            onClick={() => navigate('/aAyuiOcvMq/dashboard')}
            className="text-black opacity-60 hover:opacity-100 transition-opacity"
          >
            ← Back to Admin Dashboard
          </button>
        </div>

        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
            Neural Network Management
          </h1>
          <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 mb-6">
            Manage writeups database and monitor neural network learning performance
          </p>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="writeups">Writeups Database</TabsTrigger>
              <TabsTrigger value="analytics">Learning Analytics</TabsTrigger>
              <TabsTrigger value="settings">Network Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="writeups" className="space-y-6">
              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle>Filters</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={filters.category} onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All categories</SelectItem>
                          <SelectItem value="vulnerability">Vulnerability</SelectItem>
                          <SelectItem value="technique">Technique</SelectItem>
                          <SelectItem value="tool">Tool</SelectItem>
                          <SelectItem value="methodology">Methodology</SelectItem>
                          <SelectItem value="case-study">Case Study</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Difficulty</Label>
                      <Select value={filters.difficulty} onValueChange={(value) => setFilters(prev => ({ ...prev, difficulty: value }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="All difficulties" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All difficulties</SelectItem>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={filters.isActive.toString()} onValueChange={(value) => setFilters(prev => ({ ...prev, isActive: value === 'true' }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Active</SelectItem>
                          <SelectItem value="false">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Add Writeup Button */}
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Writeups ({writeups.length})</h2>
                <Button 
                  onClick={() => setShowAddForm(true)}
                  className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                >
                  Add Writeup
                </Button>
              </div>

              {/* Add Writeup Form */}
              {showAddForm && (
                <Card>
                  <CardHeader>
                    <CardTitle>Add New Writeup</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddWriteup} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Title *</Label>
                          <Input
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Writeup title"
                            required
                          />
                        </div>
                        <div>
                          <Label>Author</Label>
                          <Input
                            value={formData.author}
                            onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                            placeholder="Author name"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Category</Label>
                          <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value as WriteupEntry['category'] }))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="vulnerability">Vulnerability</SelectItem>
                              <SelectItem value="technique">Technique</SelectItem>
                              <SelectItem value="tool">Tool</SelectItem>
                              <SelectItem value="methodology">Methodology</SelectItem>
                              <SelectItem value="case-study">Case Study</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Difficulty</Label>
                          <Select value={formData.difficulty} onValueChange={(value) => setFormData(prev => ({ ...prev, difficulty: value as WriteupEntry['difficulty'] }))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beginner">Beginner</SelectItem>
                              <SelectItem value="intermediate">Intermediate</SelectItem>
                              <SelectItem value="advanced">Advanced</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Tags</Label>
                          <Input
                            value={formData.tags}
                            onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                            placeholder="tag1, tag2, tag3"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Content *</Label>
                        <Textarea
                          value={formData.content}
                          onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                          placeholder="Writeup content..."
                          rows={6}
                          required
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button type="submit" className="bg-[#daed6e] hover:bg-[#c5d85c] text-black">
                          Add Writeup
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Writeups List */}
              <div className="space-y-4">
                {writeups.map((writeup) => (
                  <WriteupCard writeup={writeup} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              {analytics && (
                <>
                  {/* Overview Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-black">{analytics.totalWriteups}</div>
                        <p className="text-sm text-gray-600">Total Writeups</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-black">{analytics.totalPathways}</div>
                        <p className="text-sm text-gray-600">Generated Pathways</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-black">{(analytics.averageConfidence * 100).toFixed(1)}%</div>
                        <p className="text-sm text-gray-600">Avg Confidence</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-black">
                          {analytics.topPerformingWriteups.length}
                        </div>
                        <p className="text-sm text-gray-600">Top Performers</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Performing Writeups */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Performing Writeups</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {analytics.topPerformingWriteups.map((writeup, idx) => (
                          <div key={writeup.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 rounded-full bg-[#daed6e] flex items-center justify-center text-sm font-bold">
                                {idx + 1}
                              </div>
                              <div>
                                <h4 className="font-medium text-black">{writeup.title}</h4>
                                <p className="text-sm text-gray-600">{writeup.category} • {writeup.difficulty}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className={getLearningScoreColor(writeup.learningScore)}>
                                {(writeup.learningScore * 100).toFixed(0)}%
                              </Badge>
                              <p className="text-sm text-gray-600 mt-1">Used {writeup.usageCount} times</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Category Performance */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Category Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {analytics.categoryPerformance.map((category) => (
                          <div key={category.category} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium capitalize">{category.category}</span>
                              <span className="text-sm text-gray-600">
                                {(category.averageScore * 100).toFixed(1)}% avg • {category.usageCount} uses
                              </span>
                            </div>
                            <Progress value={category.averageScore * 100} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Neural Network Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <Alert className="mb-4">
                    <AlertTitle>Neural Network Status</AlertTitle>
                    <AlertDescription>
                      The neural network is actively learning from writeups and user feedback. 
                      Performance improves as more data is collected and analyzed.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Model Version</Label>
                      <Input value="1.0.0" disabled />
                    </div>
                    <div>
                      <Label>Learning Rate</Label>
                      <Input value="0.1" disabled />
                    </div>
                    <div>
                      <Label>Minimum Confidence Threshold</Label>
                      <Input value="0.6" disabled />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <EditWriteupDialog />
      <DeleteConfirmationDialog />
    </div>
  );
}; 